import { useMutation, useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';
import { api } from '../../../singleton/axios';

interface ManualHoldingInput {
  ticker: string;
  shares: number;
  averagePrice: number;
}

interface AddManualHoldingsRequest {
  holdings: ManualHoldingInput[];
}

interface CreatedHolding {
  id: string;
  ticker: string;
  shares: number;
  averagePrice: number;
  totalAmount: number;
}

interface AddManualHoldingsResponse {
  message: string;
  created_count: number;
  holdings: CreatedHolding[];
}

async function addManualHoldings(requestData: AddManualHoldingsRequest): Promise<AddManualHoldingsResponse> {
  try {
    const { data } = await api.post<AddManualHoldingsResponse>('/portfolio/manual-holdings', requestData);
    return data;
  } catch (error: any) {
    const errorMessage = error.response?.data?.error || 'Failed to add manual holdings';
    const errorCode = error.response?.data?.code || 'UNKNOWN_ERROR';

    throw new Error(`${errorMessage} (${errorCode})`);
  }
}

export function useAddManualHoldings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: AddManualHoldingsRequest) => addManualHoldings(data),
    onSuccess: (response) => {
      // Remove portfolio queries entirely to force fresh fetch on navigation
      queryClient.removeQueries({ queryKey: ['portfolio'] });
      queryClient.removeQueries({ queryKey: ['currentPortfolio'] });
      queryClient.removeQueries({ queryKey: ['portfolioPrices'] });

      // Invalidate dividend-related queries as they depend on portfolio holdings
      queryClient.invalidateQueries({ queryKey: ['dividendAnalysis'] });
      queryClient.invalidateQueries({ queryKey: ['dividendStats'] });
      queryClient.invalidateQueries({ queryKey: ['portfolioDividendStats'] });
      queryClient.invalidateQueries({ queryKey: ['dividendCalendar'] });

      // Show success notification
      const count = response.created_count;
      const message = count === 0
        ? 'All manual holdings removed successfully!'
        : `${count} ${count === 1 ? 'holding' : 'holdings'} saved successfully!`;
      enqueueSnackbar(message, {
        variant: 'success',
        autoHideDuration: 3000,
      });
    },
    onError: (error: Error) => {
      // Show error notification
      enqueueSnackbar(error.message || 'Failed to add manual holdings', {
        variant: 'error',
        autoHideDuration: 5000,
      });
    },
  });
}

export type { ManualHoldingInput, AddManualHoldingsRequest, AddManualHoldingsResponse };